<template>
  <div class="container">
    <p class="text-muted" v-if="loading === 'user'">
      <i class="fa fa-spinner fa-spin"/> Loading info for user {{ userUuid }}
    </p>

    <div class="alert alert-danger" v-if="error">
      <h4>Error Loading User Events: {{ error }}</h4>
    </div>

    <template v-if="!loading">
      <h1 class="light">{{ accountInfo.name }}: <span class="text-muted">{{ accountInfo.email }}</span></h1>
      <div class="row">
        <div class="col-sm-6">
          <p><a :href="`#!/user_info?email=${accountInfo.email}`">View user details</a></p>
          <p><b>Title:</b> <span class="text-muted"> {{ accountInfo.title || '(unspecified)' }} </span></p>
          <p><b>Role:</b> <span class="text-muted"> {{ accountInfo.role || '(unspecified)' }}</span></p>
        </div>
        <div class="col-sm-6">
          <p><b>Level:</b> <span class="text-muted"> {{ accountInfo.level || '(unspecified)' }}</span></p>
          <p><b>District Name:</b> <span class="text-muted"> {{ accountInfo.district_name || '(unspecified)' }}</span></p>
          <p><b>School Name:</b> <span class="text-muted"> {{ accountInfo.school_name || '(unspecified)' }}</span></p>
        </div>
      </div>
      <hr>

      <div class="row">
        <!-- Event Listing Table -->
        <div class="col-md-8">
          <h2>
            <button
              class="btn btn-default pull-right"
              @click="refresh"
              :disabled="!!loading">Refresh</button>
            Event List <small class="text-muted">({{ this.events.length }} Events)</small>
          </h2>
          <p class="text-info"><i class="fa fa-info-circle"/> Events database is updated once a day. Last update: {{ tableRefreshedAtFormatted }}</p>

          <v-client-table
            v-if="events.length > 0"
            :columns="eventsTableColumns"
            :data="events"
            :options="eventsTableOptions">
            <b slot="name" slot-scope="props">
              {{ props.row.name }}
            </b>
            <div slot="product" slot-scope="props">
              {{ props.row.source }}
            </div>
            <div slot="time" slot-scope="props">
              {{ (new Date(props.row.utc)).toLocaleDateString('en-US') }}
              <br>
              {{ (new Date(props.row.utc)).toLocaleTimeString('en-US') }}
            </div>
            <div slot="data" slot-scope="props">
              <a
                role="button"
                @click="toggleDetails(props.index)">show/collapse</a>

              <collapse v-model="showDetails[props.index]">
                <dl :id="`${props.index}-evt-data`">
                  <template v-for="(val, key) in JSON.parse(props.row.data)" >
                    <dt :key="key" v-if="val">{{ key }}</dt>
                    <dd :key="`${key}-val`" v-if="val">{{ val }}</dd>
                  </template>
                </dl>
              </collapse>
            </div>
          </v-client-table>
          <br>
          <button
            type="button"
            class="btn btn-default"
            @click="loadMoreEvents(lastEventUTC)"
            v-if="!loading && canLoadMore">Load More Events</button>
          <p class="loading text-muted" v-if="loading === 'events'">
            <i class="fa fa-spinner fa-spin"/> Loading events...
          </p>
        </div>

        <!-- Aggregation Table -->
        <div class="col-md-4">
          <h2>Event Aggregation</h2>
          <p class="text-info"><i class="fa fa-info-circle"/> Only aggregates the events that are shown on the left</p>
          <table class="table table-striped table-condensed" v-if="events.length > 0">
            <thead>
              <tr>
                <th scope="col">Event Name</th>
                <th scope="col">Event Count</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="event in aggregations" :key="event.name">
                <th scope="row">{{ event.name }}</th>
                <td>{{ event.count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

  </div>
</template>

<script>
import { ClientTable } from 'vue-tables-2'
import Vue from 'vue'
Vue.use(ClientTable)
import { Collapse } from 'uiv'

const EVENT_BATCH_LIMIT = 500

export default {
  name: 'UserEvents',
  components: {
    Collapse
  },
  props: {
    userUuid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      events: [],
      eventsTableColumns: ['name', 'product', 'time', 'data'],
      eventsTableOptions: {
        headings: {
          name: 'Event Name',
          product: 'Product',
          time: 'Time',
          data: 'Data'
        },
        sortable: [],
        filterByColumn: true,
        filterable: ['name'],
        perPage: 100,
        perPageValues: [50, 100, 300, 500]
      },
      showDetails: {},
      numEventsInLastBatch: 0,
      accountInfo: {},
      loading: 'user',
      error: false,
      tableRefreshedAt: ''
    }
  },
  computed: {
    aggregations() {
      var eventCounts = this.events.reduce((accumulator, currentEvent) => {
        if (!accumulator[currentEvent.name]) {
          accumulator[currentEvent.name] = 0
        }
        accumulator[currentEvent.name] += 1
        return accumulator
      }, {})

      return Object.keys(eventCounts)
        .map(eventName => {
          return {
            name: eventName,
            count: eventCounts[eventName]
          }
        })
        .sort((a, b) => {
          return b.count - a.count
        })
    },
    lastEventUTC() {
      return this.events.length > 0 ? this.events[this.events.length - 1].utc : '2100-01-01'
    },
    canLoadMore() {
      return this.numEventsInLastBatch >= EVENT_BATCH_LIMIT
    },
    tableRefreshedAtFormatted() {
      var refreshDate = new Date(this.tableRefreshedAt)
      return (
        refreshDate.getMonth() +
        1 +
        '/' +
        refreshDate.getDate() +
        '/' +
        refreshDate.getFullYear() +
        ' ' +
        (refreshDate.getHours() + 1) +
        ':' +
        refreshDate.getMinutes()
      )
    }
  },
  methods: {
    async loadMoreEvents(beforeDate = '2100-01-01') {
      // Sdate just needs to be the UTC of the last event or some date in the future if we have no events yet
      // Because we limit the number of events returned, eDate doesn't matter
      var sDate = beforeDate
      var eDate = '0001-01-01'
      this.loading = 'events'
      try {
        const report = await this.$angularWorld.analyticsApi.getUserReport(
          'user_events',
          this.userUuid,
          sDate,
          eDate,
          'redshift'
        )
        this.handleReport(report)
      } catch (err) {
        this.handleError(err)
      }
    },
    refresh() {
      this.events = []
      this.loadMoreEvents()
    },
    handleReport(report) {
      let moreEvents = report[0].Report || []
      this.numEventsInLastBatch = moreEvents.length
      this.events = this.events.concat(moreEvents)
      this.loading = ''
      this.tableRefreshedAt = report[0].refreshed_at
    },
    handleError(err) {
      this.error = JSON.stringify(err)
      this.loading = ''
    },
    toggleDetails(index) {
      this.$set(this.showDetails, index, !this.showDetails[index])
    }
  },
  created() {
    this.$angularWorld.adminApi.getUserInfo('', this.userUuid).then(userInfo => {
      this.accountInfo = userInfo.user_account
      this.loading = 'events'
      this.loadMoreEvents()
    })
  }
}
</script>

<style scoped>
p.loading {
  clear: both;
}
td {
  word-break: break-all;
  min-width: 90px;
}
</style>
